import { makeAutoObservable, runInAction } from 'mobx';

import Dto from '~/base/Dto';
import { IBaseStore } from '~/base/types/BaseTypes';

import AuthService from './AuthService';
import { SignInForm } from './forms/SignInForm';
import AccessTokenService from './modules/accessToken/AccessTokenService';
import RefreshTokenService from './modules/refreshToken/RefreshTokenService';
import { RefreshTokenDto } from './modules/refreshToken/dto/RefreshTokenDto';
import { AuthAccessType } from './types/AuthTypes';

export class AuthStore implements IBaseStore {
  apiUrl: string | null = null;
  accessToken: string | null = null;
  refreshToken: string | null = null;

  singInLoading: boolean = false;
  signInForm: SignInForm = SignInForm.createObservable();
  isOpenAuthModal: boolean = false;

  logout: () => void = () => {};

  private authService: AuthService;
  private accessTokenService: AccessTokenService;
  private refreshTokenService: RefreshTokenService;

  constructor() {
    makeAutoObservable(this);
    this.authService = new AuthService();
    this.accessTokenService = new AccessTokenService();
    this.refreshTokenService = new RefreshTokenService();
  }

  // INIT

  init = () => {
    this.checkAuth();
  };

  checkAuth = (): void => {
    // Этот кейс будет когда родительский проект передает токен
    if (this.accessToken && this.refreshToken) {
      return;
    }

    // А токен может НЕ быть только при отдельном запуске локально
    const accessToken = this.accessTokenService.get();
    const refreshToken = this.refreshTokenService.get();

    if (accessToken && refreshToken) {
      this.accessTokenService.set(accessToken);
      this.refreshTokenService.set(refreshToken);

      this.setAuthTokens(accessToken, refreshToken);
      return;
    }

    if (import.meta.env.DEV) {
      this.setIsOpenAuthModal(true);
    }
  };

  // API

  // Для авторизации во время разработки
  signIn = (form: SignInForm) => {
    this.singInLoading = true;

    this.authService
      .signIn(form)
      .then(({ token, refreshToken }) =>
        runInAction(() => {
          if (!token || !refreshToken) {
            return;
          }

          this.setAuthTokens(token, refreshToken);
          this.isOpenAuthModal = false;
        }),
      )
      .catch(() => {})
      .finally(() =>
        runInAction(() => {
          this.singInLoading = false;
        }),
      );
  };

  // Метод нужен локально во время разработки, после сборки, после интеграции за обновление токена отвечает родительский проект
  refreshAccessToken = async (): Promise<AuthAccessType | null> => {
    const accessToken = this.accessToken;
    const refreshToken = this.refreshToken;

    if (!accessToken || !refreshToken) {
      return null;
    }

    const dto = Dto.populate(RefreshTokenDto, {
      accessToken,
      refreshToken,
    });

    return this.refreshTokenService
      .refreshAccessToken(dto)
      .then((res) => {
        if (res.token && res.refreshToken) {
          this.setAuthTokens(res.token, res.refreshToken);
          return { accessToken: res.token, refreshToken: res.refreshToken };
        }

        return null;
      })
      .catch(() => {
        return null;
      });
  };

  // RESET

  reset = () => {
    this.accessToken = null;
  };

  // GETTERS

  getTokens = (): { accessToken: string | null; refreshToken: string | null } => {
    return {
      accessToken: this.accessTokenService.get(),
      refreshToken: this.refreshTokenService.get(),
    };
  };

  // SETTERS

  setAuthTokens = (accessToken: string, refreshToken: string) => {
    this.accessToken = accessToken;
    this.accessTokenService.set(accessToken);

    this.refreshToken = refreshToken;
    this.refreshTokenService.set(refreshToken);
  };

  setIsOpenAuthModal = (value: boolean) => {
    this.isOpenAuthModal = value;
  };
}
